/**
 * Ops Portal - Reports Page
 * Generate, view, and export operational reports with chart visualizations
 */
import { useState, useEffect } from 'react';
import { opsApi } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Tabs, TabsContent, TabsList, TabsTrigger
} from '@/components/ui/tabs';
import {
  FileText, Download, Calendar, TrendingUp, Thermometer,
  Package, Clock, Loader2, BarChart3, RefreshCw, AlertTriangle,
  FileDown, FileSpreadsheet, FileType
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, BarChart, Bar, AreaChart, Area
} from 'recharts';

// Custom tooltip for charts
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-slate-900 border border-slate-700 rounded-lg p-3 shadow-lg">
        <p className="text-white font-medium mb-2">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color }} className="text-sm">
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export default function OpsReportsPage() {
  const [loading, setLoading] = useState(true);
  const [reportType, setReportType] = useState('temperature');
  const [dateRange, setDateRange] = useState('7');
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState({
    totalDeliveries: 0,
    avgDeliveryTime: '-',
    tempCompliance: '-',
    onTimeRate: '-'
  });
  
  // Chart data states
  const [temperatureHistory, setTemperatureHistory] = useState([]);
  const [alertHistory, setAlertHistory] = useState([]);
  const [deviceUptime, setDeviceUptime] = useState([]);
  const [chartLoading, setChartLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchReports();
    fetchChartData();
  }, [reportType, dateRange]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      // Fetch summary stats
      const summaryRes = await opsApi.getReportsSummary(parseInt(dateRange));
      setStats(summaryRes.data);
      
      // Fetch specific report type
      let reportsRes;
      switch (reportType) {
        case 'temperature':
          reportsRes = await opsApi.getTemperatureReports(parseInt(dateRange));
          break;
        case 'delivery':
          reportsRes = await opsApi.getDeliveryReports(parseInt(dateRange));
          break;
        case 'device':
          reportsRes = await opsApi.getDeviceReports();
          break;
        case 'alerts':
          reportsRes = await opsApi.getAlertReports(parseInt(dateRange));
          break;
        default:
          reportsRes = { data: [] };
      }
      
      setReports(reportsRes.data);
    } catch (error) {
      console.error('Failed to load reports:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const fetchChartData = async () => {
    setChartLoading(true);
    try {
      const days = parseInt(dateRange);
      const [tempRes, alertRes, deviceRes] = await Promise.all([
        opsApi.getTemperatureHistory(days),
        opsApi.getAlertHistory(days),
        opsApi.getDeviceUptime(days)
      ]);
      
      setTemperatureHistory(tempRes.data);
      setAlertHistory(alertRes.data);
      setDeviceUptime(deviceRes.data);
    } catch (error) {
      console.error('Failed to load chart data:', error);
    } finally {
      setChartLoading(false);
    }
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const API_BASE = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('trackotic_token');
      const url = `${API_BASE}/api/v1/reports/export/${format}/${reportType}?days=${dateRange}`;
      
      // Create hidden link and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reportType}_report.${format}`);
      
      // Add auth header via fetch for download
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Export failed');
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      link.href = downloadUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success(`${format.toUpperCase()} export downloaded successfully`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error(`Failed to export ${format.toUpperCase()}`);
    } finally {
      setExporting(false);
    }
  };

  const getReportIcon = (type) => {
    switch (type) {
      case 'temperature': return <Thermometer className="w-4 h-4 text-blue-400" />;
      case 'delivery': return <Package className="w-4 h-4 text-green-400" />;
      case 'device': return <BarChart3 className="w-4 h-4 text-purple-400" />;
      case 'alerts': return <AlertTriangle className="w-4 h-4 text-amber-400" />;
      default: return <FileText className="w-4 h-4 text-slate-400" />;
    }
  };

  const getReportDetails = (report) => {
    switch (report.type) {
      case 'temperature':
        return `${report.records} readings, ${report.breaches} breaches, Avg: ${report.avgTemp}`;
      case 'delivery':
        return `${report.completed} completed, ${report.pending} pending, ${report.failed} failed`;
      case 'device':
        return `${report.online} online, ${report.offline} offline, ${report.battery_low} low battery`;
      case 'alerts':
        return `${report.total} total, ${report.critical} critical, ${report.resolved} resolved`;
      default:
        return '-';
    }
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <FileText className="w-8 h-8 text-teal-500" />
            Reports & Analytics
          </h1>
          <p className="text-slate-400 mt-1">Generate, visualize, and export operational reports</p>
        </div>
        <div className="flex gap-3">
          <Button 
            onClick={() => handleExport('csv')} 
            disabled={exporting}
            variant="outline"
            className="border-slate-700 text-slate-300 hover:bg-slate-800"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button 
            onClick={() => handleExport('pdf')} 
            disabled={exporting}
            variant="outline"
            className="border-slate-700 text-slate-300 hover:bg-slate-800"
          >
            <FileType className="w-4 h-4 mr-2" />
            Export PDF
          </Button>
          <Button onClick={() => { fetchReports(); fetchChartData(); }} className="bg-teal-600 hover:bg-teal-700">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
          <div className="flex items-center gap-3 mb-2">
            <Package className="w-5 h-5 text-green-400" />
            <span className="text-slate-400 text-sm">Total Deliveries</span>
          </div>
          <p className="text-2xl font-bold text-white">{stats.totalDeliveries}</p>
          <p className="text-green-400 text-sm mt-1">Last {dateRange} days</p>
        </div>
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
          <div className="flex items-center gap-3 mb-2">
            <Clock className="w-5 h-5 text-blue-400" />
            <span className="text-slate-400 text-sm">Avg Delivery Time</span>
          </div>
          <p className="text-2xl font-bold text-white">{stats.avgDeliveryTime}</p>
          <p className="text-blue-400 text-sm mt-1">Across all routes</p>
        </div>
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
          <div className="flex items-center gap-3 mb-2">
            <Thermometer className="w-5 h-5 text-cyan-400" />
            <span className="text-slate-400 text-sm">Temp Compliance</span>
          </div>
          <p className="text-2xl font-bold text-white">{stats.tempCompliance}</p>
          <p className="text-cyan-400 text-sm mt-1">{stats.tempBreaches || 0} breaches</p>
        </div>
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
          <div className="flex items-center gap-3 mb-2">
            <TrendingUp className="w-5 h-5 text-purple-400" />
            <span className="text-slate-400 text-sm">On-Time Rate</span>
          </div>
          <p className="text-2xl font-bold text-white">{stats.onTimeRate}</p>
          <p className="text-purple-400 text-sm mt-1">Delivery performance</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <Select value={reportType} onValueChange={setReportType}>
          <SelectTrigger className="w-48 bg-slate-900 border-slate-800 text-white" data-testid="report-type-select">
            <SelectValue placeholder="Report Type" />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800">
            <SelectItem value="temperature">Temperature Reports</SelectItem>
            <SelectItem value="delivery">Delivery Reports</SelectItem>
            <SelectItem value="device">Device Reports</SelectItem>
            <SelectItem value="alerts">Alert Reports</SelectItem>
          </SelectContent>
        </Select>
        <Select value={dateRange} onValueChange={setDateRange}>
          <SelectTrigger className="w-40 bg-slate-900 border-slate-800 text-white" data-testid="date-range-select">
            <SelectValue placeholder="Date Range" />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800">
            <SelectItem value="1">Last 24 Hours</SelectItem>
            <SelectItem value="7">Last 7 Days</SelectItem>
            <SelectItem value="30">Last 30 Days</SelectItem>
            <SelectItem value="90">Last 90 Days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Tabs for Charts and Table */}
      <Tabs defaultValue="charts" className="space-y-6">
        <TabsList className="bg-slate-900 border border-slate-800">
          <TabsTrigger value="charts" className="data-[state=active]:bg-teal-600">
            <BarChart3 className="w-4 h-4 mr-2" />
            Charts
          </TabsTrigger>
          <TabsTrigger value="table" className="data-[state=active]:bg-teal-600">
            <FileText className="w-4 h-4 mr-2" />
            Reports
          </TabsTrigger>
        </TabsList>

        {/* Charts Tab */}
        <TabsContent value="charts" className="space-y-6">
          {chartLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Temperature Trend Chart */}
              <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Thermometer className="w-5 h-5 text-cyan-400" />
                  Temperature Trends
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={temperatureHistory}>
                    <defs>
                      <linearGradient id="colorAvg" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#0d9488" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#0d9488" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="label" stroke="#94a3b8" fontSize={12} />
                    <YAxis stroke="#94a3b8" fontSize={12} domain={[-5, 15]} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="avgTemp"
                      name="Avg Temp (°C)"
                      stroke="#0d9488"
                      fill="url(#colorAvg)"
                      strokeWidth={2}
                    />
                    <Line
                      type="monotone"
                      dataKey="minTemp"
                      name="Min Temp"
                      stroke="#3b82f6"
                      strokeWidth={1}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="maxTemp"
                      name="Max Temp"
                      stroke="#ef4444"
                      strokeWidth={1}
                      dot={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Alert Frequency Chart */}
              <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-400" />
                  Alert Frequency
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={alertHistory}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="label" stroke="#94a3b8" fontSize={12} />
                    <YAxis stroke="#94a3b8" fontSize={12} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar dataKey="critical" name="Critical" fill="#ef4444" stackId="a" />
                    <Bar dataKey="warning" name="Warning" fill="#f59e0b" stackId="a" />
                    <Bar dataKey="info" name="Info" fill="#3b82f6" stackId="a" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Device Uptime Chart */}
              <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-purple-400" />
                  Device Uptime
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={deviceUptime}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="label" stroke="#94a3b8" fontSize={12} />
                    <YAxis stroke="#94a3b8" fontSize={12} domain={[0, 100]} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="uptimePercent"
                      name="Uptime %"
                      stroke="#8b5cf6"
                      strokeWidth={2}
                      dot={{ fill: '#8b5cf6', strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Temperature Breaches Chart */}
              <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Thermometer className="w-5 h-5 text-red-400" />
                  Temperature Breaches
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={temperatureHistory}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="label" stroke="#94a3b8" fontSize={12} />
                    <YAxis stroke="#94a3b8" fontSize={12} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar dataKey="readings" name="Total Readings" fill="#0d9488" />
                    <Bar dataKey="breaches" name="Breaches" fill="#ef4444" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Reports Table Tab */}
        <TabsContent value="table">
          <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-800">
                  <TableHead className="text-slate-400">Report</TableHead>
                  <TableHead className="text-slate-400">Date</TableHead>
                  <TableHead className="text-slate-400">Details</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                  <TableHead className="text-slate-400">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-teal-500 mx-auto" />
                    </TableCell>
                  </TableRow>
                ) : reports.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-12 text-slate-500">
                      No reports available for this period
                    </TableCell>
                  </TableRow>
                ) : (
                  reports.map((report) => (
                    <TableRow key={report.id} className="border-slate-800">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {getReportIcon(report.type)}
                          <span className="text-white">{report.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-300">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-slate-500" />
                          {report.date}
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-400 text-sm">
                        {getReportDetails(report)}
                      </TableCell>
                      <TableCell>
                        <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                          {report.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleExport('csv')}
                            className="text-teal-400 hover:text-teal-300"
                            data-testid={`download-csv-${report.id}`}
                          >
                            <FileSpreadsheet className="w-4 h-4 mr-1" />
                            CSV
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleExport('pdf')}
                            className="text-purple-400 hover:text-purple-300"
                            data-testid={`download-pdf-${report.id}`}
                          >
                            <FileType className="w-4 h-4 mr-1" />
                            PDF
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
